import { useCallback, useMemo, useState } from "react"
import { Box, Card, IconButton, MenuItem, Select, Skeleton, Stack, StackProps, Typography } from '@mui/material'

import {
	SisetuMatchingFormData, SisetuMatchingResult
} from "interface/sisetu/match"
import {
  Comment as MemoIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  CheckBoxOutlineBlank as InCompletedIcon,
  CheckBox as CompletedIcon
} from '@mui/icons-material'

import { Loading } from "components/controls/loading"

import { KojinMatchingCandidate } from "./kojinCandidate"
import { SisetuMatchingCandidate } from "./sisetuCandidate"
import { KojinSelfMatchingDialog } from "../dialog/kojinSelfMatching"
import { SisetuSelfMatchingDialog } from "../dialog/sisetuSelfMatching"
import { EditMemoDialog } from "../dialog/editMemo"

import { ErrorInfo } from "components/controls/errorInfo"
import { useMatching } from "../container"
import { CopyIcon, SearchIcon } from "shared/icons"
import { KojinMatchingFormData, KojinMatchingResult } from "interface/kojin/match"
import { useMatchingRequest } from "interface/matching"
import { KojinResult } from "interface/kojin"
import { SisetuResult } from "interface/sisetu"

const googleSearchUrl = 'https://www.google.com/search?q='

const KojinMatchingFormPanel = ({ form }: { form: KojinMatchingFormData }) => {
  return (
    <>
      {form.affiliation?.split('\n').map(
        (affiliation, index) => (
          <Typography
            key={`affiliation-${index}`}
            width="100%"
            variant="body2"
            component="p"
          >
            {affiliation}
          </Typography>
        )
      )}
    </>
  )
}
const KojinCandidateSelect = ({
  choice,
  isMultiHit,
  onChoice,
  candidates
}: {
  choice: string | null
  isMultiHit: boolean
  onChoice: (kojinCode: string | null) => void
  candidates: KojinResult[]
}) => {
  return (
    <Select
      fullWidth
      size="small"
      value={choice ?? 'none'}
      variant="outlined"
      onChange={(e) => onChoice(e.target.value === 'none' ? null : e.target.value)}
      sx={{
        color: choice === null ? '#c8c8c8' : undefined
      }}
    >
      <MenuItem value={'none'}>該当なし</MenuItem>
      {candidates.map((kojin, i) => (
        <MenuItem
          key={`${i}-${kojin.kojinCode}`}
          value={kojin.kojinCode}
        >
          <KojinMatchingCandidate kojin={kojin} isMultiHit={isMultiHit} width="100%"/>
        </MenuItem>
      ))}
    </Select>
  )
}

const SisetuMatchingFormPanel = ({ form }: { form: SisetuMatchingFormData }) => {
  return (
    <>
      {form.todofuken && (
        <Typography width="100%" variant="body2">
          {form.todofuken}
        </Typography>
      )}
    </>
  )
}
const SisetuCandidateSelect = ({
  choice,
  isMultiHit,
  onChoice,
  candidates
}: {
  choice: string | null
  isMultiHit: boolean
  onChoice: (sisetuCode: string | null) => void
  candidates: SisetuResult[]
}) => {
  return (
    <Select
      fullWidth
      size="small"
      value={choice ?? 'none'}
      variant="outlined"
      onChange={(e) => onChoice(e.target.value === 'none' ? null : e.target.value)}
      sx={{ color: choice === null ? '#c8c8c8' : undefined }}
    >
      <MenuItem value={'none'}>該当なし</MenuItem>
      {candidates.map((sisetu, i) => (
        <MenuItem
          key={`${i}-${sisetu.sisetuCode}`}
          value={sisetu.sisetuCode}
        >
          <SisetuMatchingCandidate sisetu={sisetu} isMultiHit={isMultiHit} width="100%"/>
        </MenuItem>
      ))}
    </Select>
  )
}

type MatchingRecordProps = {
  uuid: string
	index: number
  container: ReturnType<typeof useMatching> 
} & StackProps
export const MatchingRecord = ({
  uuid,
	index,
  container: {
    setMatchingForm, getMatchingForm,
		setMatchingResult, getMatchingResult, onMatched,
    setMemo, getMemo,
		autoMatchingMode, autoMatchingIndex,
    setOpenDcfCode,
    completedIndex,
    setCompletedIndex
  },
  ...others
}: MatchingRecordProps) => {
  const [modal, setModal] = useState<'selfMatch' | 'editMemo' | null>(null)

  const formData = useMemo(() => getMatchingForm(uuid), [getMatchingForm, uuid])
  const result = useMemo(() => getMatchingResult(uuid), [getMatchingResult, uuid])
  const memo = useMemo(() => getMemo(uuid) ?? '', [getMemo, uuid])

  const searchWord = useMemo(
    () => {
      const name = formData?.form.name.trim()
      if (!name) return ''

      const words = [name]
      if (formData?.target === 'kojin') {
        const kojinFormData = formData?.form as KojinMatchingFormData
        const affiliations = kojinFormData.affiliation?.split('\n').flatMap(aff => {
          const trimed = aff.trim()
          return trimed.length > 0 ? trimed : []
        }) ?? []
        if (affiliations.length > 0) words.push(...affiliations)
        if (kojinFormData.senmon) {
          const trimed = kojinFormData.senmon.trim()
          if (trimed.length > 0) words.push(trimed)
        }
      } else if (formData?.target === 'sisetu') {
        const sisetuFormData = formData?.form as SisetuMatchingFormData
        if (sisetuFormData.todofuken) words.push(sisetuFormData.todofuken)
      }
      return words.map(word => `"${word}"`).join(' ')
    },
    [formData]
  )

  type SelfMatchingParam = {
    target: 'kojin' | 'sisetu'
    uuid: string
  }
  const onSelfMatched = useCallback(
    (result: KojinMatchingResult | SisetuMatchingResult, param: SelfMatchingParam) => {
      onMatched(param.target, param.uuid, result)
    },
    [onMatched]
  )
	const [matchingState, matching] = useMatchingRequest<SelfMatchingParam>({
    onPosted: onSelfMatched
  })
  const doSelfMatching = useCallback(
    (target: 'kojin' | 'sisetu', form: KojinMatchingFormData | SisetuMatchingFormData) => {
      setMatchingForm(uuid, form)
      matching({ target, form, params: { target, uuid }})
    },
    [uuid, setMatchingForm, matching]
  )

  const onChoiceKojin = useCallback(
    (dcfCode: string | null) => {
      if (!result) return
      const kojin = result.result.candidates.find(kojin => (kojin as KojinResult).kojinCode === dcfCode) as KojinResult | undefined
      setMatchingResult(result.target, uuid, { ...result.result, kojin, dcfCode })
    },
    [uuid, result, setMatchingResult]
  )
  const onChoiceSisetu = useCallback(
    (dcfCode: string | null) => {
      if (!result) return
      const sisetu = result.result.candidates.find(sisetu => (sisetu as SisetuResult).sisetuCode === dcfCode) as SisetuResult | undefined
      setMatchingResult(result.target, uuid, { ...result.result, sisetu, dcfCode })
    },
    [uuid, result, setMatchingResult]
  )

  const onEditMemo = useCallback(
    (memo: string) => {
      setMemo(uuid, memo)
    },
    [uuid, setMemo]
  )

  const onCopyDcfCode = useCallback(
    () => {
      if (!result || !result.result.dcfCode) return
      navigator.clipboard.writeText(result.result.dcfCode)
    },
    [result]
  )

  const onOpenDcfCode = useCallback(
    () => {
      if (!result || !result.result.dcfCode) return
      setOpenDcfCode(result.result.dcfCode)
    },
    [result, setOpenDcfCode]
  )

  const closeModal = useCallback(
    () => setModal(null),
    [setModal]
  )
  const openSelfMatch = useCallback(
    () => setModal('selfMatch'),
    [setModal]
  )
  const editMemo = useCallback(
    () => setModal('editMemo'),
    [setModal]
  )

  return (
    <Stack
      alignItems="stretch"
      direction="row"
      spacing={1}
      boxSizing="border-box"
      width='100%'
      {...others}
    >
      <Box paddingY={0.5}>
        <IconButton
          size="small"
          onClick={() => {
            setCompletedIndex(index + 1)
          }}
        >
          {index < completedIndex ? (
            <CompletedIcon fontSize="small" color="primary"/>
          ) : (
            <InCompletedIcon fontSize="small"/>
          )}
        </IconButton>
      </Box>
      <Card
        variant="outlined"
        sx={{
          flexGrow: 1
        }}
      >
        <Stack
          alignItems="flex-start"
          direction="row"
          spacing={1}
          padding={1}
          justifyContent="flex-start"
          boxSizing="border-box"
        >
          <Typography variant="body1">{index+1}</Typography>

          <Stack
            alignItems="flex-start"
            direction="column"
            spacing={0.5}
            justifyContent="flex-start"
            flexGrow={1}
          >
            <Typography width="100%" variant="body1">
              {formData?.form.name}
            </Typography>
            {formData?.target === 'kojin' ? (
              <KojinMatchingFormPanel form={formData.form as KojinMatchingFormData}/>
            ) : formData?.target === 'sisetu' ? (
              <SisetuMatchingFormPanel form={formData.form as SisetuMatchingFormData}/>
            ) : <></>}
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
          >
            <IconButton size="small" href={`${googleSearchUrl}${searchWord}`} target="_blank">
              <SearchIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={editMemo}>
              <MemoIcon fontSize="small" color={memo !== '' ? 'success' : 'disabled'} />
            </IconButton>
            <IconButton size="small" onClick={openSelfMatch}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </Card>

      <Box
        justifyContent="flex-start"
        position='relative'
        width='66%'
        flexShrink={0}
      >
        {result === undefined ? (
          autoMatchingMode === 'matching' ? (
            autoMatchingIndex !== index ? (
              <Skeleton variant="rounded" width="100%" height="100%" />
            ) : (
              <Loading/>
            )
          ) : <></>
        ) : matchingState.posting ? (
          <Loading/>
        ) : matchingState.error ? (
          <ErrorInfo error={matchingState.error}/>
        ) : result.target === 'kojin' ? (
          result.result.candidates.length === 0 ? (
            <Typography sx={{ color: '#c8c8c8' }}>
              該当する医師が見つかりません
            </Typography>
          ) : (
            <KojinCandidateSelect
              choice={result.result.dcfCode}
              onChoice={dcfCode => onChoiceKojin(dcfCode)}
              isMultiHit={result.result.isMultiHit}
              candidates={(result.result.candidates as KojinResult[])}
            />
          )
        ) : result.target === 'sisetu' ? (
          result.result.candidates.length === 0 ? (
            <Typography sx={{ color: '#c8c8c8' }}>
              該当する施設が見つかりません
            </Typography>
          ) : (
            <SisetuCandidateSelect
              choice={result.result.dcfCode}
              onChoice={dcfCode => onChoiceSisetu(dcfCode)}
              isMultiHit={result.result.isMultiHit}
              candidates={(result.result.candidates as SisetuResult[])}
            />
          )
        ) : <></>}
      </Box>

      <Stack direction="row" alignItems="flex-start">
        <IconButton
          disabled={result?.result.dcfCode ? false : true}
          size="small"
          onClick={onCopyDcfCode}
        >
          <CopyIcon fontSize="small" />
        </IconButton>

        <IconButton
          disabled={result?.result.dcfCode ? false : true}
          size="small"
          onClick={onOpenDcfCode}
        >
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      </Stack>

      {modal === 'selfMatch' && formData ? (
        formData.target === 'kojin' ? (
          <KojinSelfMatchingDialog
            formData={formData.form}
            //defaultFormData={defaultFormData}
            onSubmit={(form) => {
              doSelfMatching('kojin', form)
              closeModal()
            }}
            onClose={closeModal}
          />
        ) : formData.target === 'sisetu' ? (
          <SisetuSelfMatchingDialog
            formData={formData.form}
            //defaultFormData={defaultFormData}
            onSubmit={(form) => {
              doSelfMatching('sisetu', form)
              closeModal()
            }}
            onClose={closeModal}
          />
        ) : <></>
      ) : modal === 'editMemo' ? (
        <EditMemoDialog
          memo={memo ?? ''}
          onSubmit={(memo) => {
            onEditMemo(memo)
            closeModal()
          }}
          onClose={closeModal}
        />
      ) : <></>}
    </Stack>
  )
}